import { colors } from "app/popleads/components/ui-kit/colors";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useExtensionContext } from "../ExtensionContext";
import { createDealViewSideBar } from "../utils/deal-utils";
import { DEAL_CANCELLED, DEAL_LOST, DEAL_WON, POPUP_CONTENTS } from "../utils/constants";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const List = styled.ul`
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    padding: 0;
    list-style: none;
    height: 100%;
    overflow-y: auto;
`;

const ListItem = styled.li`
    padding: 0 20px;
    line-height: 32px;
    color: ${({ color }) => color};
    &::before {
        content: "●";
        font-size: 1.2em;
        display: inline-block;
        line-height: 12px;
        padding-right: 14px;
    }
`;

const BulletlessListItem = styled.li`
    padding: 10px 20px;
    cursor: pointer;
    color: ${colors.neutral[600]};
    background-color: ${({ isActive }) => (isActive ? colors.neutral[100] : "transparent")};
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:hover {
        background-color: ${colors.neutral[100]};
    }
    width: 100%;
`;

const DealInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const DealName = styled.div`
    width: ${({ width }) => width || "174px"};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 10px;
    height: 16px;
`;

const DealInfo = styled.div`
    font-size: 10px;
    font-weight: 400;
    line-height: 1;
`;

const DealDurum = styled.div`
    color: ${({ color }) => color};
    width: 30px;
    margin-left: 8px;
`;

const ExtensionDealSideBar = ({ ...props }) => {
    const ref = useRef(null);
    const {
        profile,
        setState,
        setSelectedDeal,
        selectedDeal,
        isDealEditing,
        setIsDealEditing,
        popupContent,
    } = useExtensionContext();
    const items = createDealViewSideBar(profile?.olanak);
    const textColors = ["#00856A", "#8C0ED9", "#6F8086", "#E76969"];

    useEffect(() => {
        if (popupContent !== POPUP_CONTENTS.NEW_DEAL && !selectedDeal && items?.length > 0) {
            const firstNonEmptyDeals = items.find((item) => item.deals?.length > 0);
            if (firstNonEmptyDeals) {
                setSelectedDeal(firstNonEmptyDeals.deals[0]);
            }
        }
    }, [items, selectedDeal, setSelectedDeal]);

    // This effect ensures that the selected deal is visible in the sidebar
    useEffect(() => {
        const selectedDealElement = document.getElementById(`dealItem-${selectedDeal?.id}`);
        if (selectedDealElement && ref.current) {
            const refRect = ref.current.getBoundingClientRect();
            const elementRect = selectedDealElement.getBoundingClientRect();
            const isElementVisible = elementRect.top >= refRect.top && elementRect.bottom <= refRect.bottom;
            if (!isElementVisible) {
                selectedDealElement.scrollIntoView({ behavior: "smooth" });
            }
        }
    }, [selectedDeal]);

    const hasDeals = items.some((item) => item.deals.length > 0);

    return (
        <>
            {hasDeals ? (
                <List as={"nav"} {...props} ref={ref}>
                    {items.map((item, index) => (
                        <React.Fragment key={index}>
                            {item.deals.length > 0 && <ListItem color={textColors[index]}>{item.name}</ListItem>}
                            {item.deals.map((deal, index2) => {
                                let dealNameWidth = 174;
                                if (!deal.campaign) {
                                    if ([DEAL_WON, DEAL_LOST, DEAL_CANCELLED].includes(deal.durum)) {
                                        dealNameWidth = 150;
                                    } else {
                                        dealNameWidth = 130;
                                    }
                                } else if (![DEAL_WON, DEAL_LOST, DEAL_CANCELLED].includes(deal.durum)) {
                                    dealNameWidth = 141;
                                }
                                return (
                                    <BulletlessListItem
                                        key={index2}
                                        style={{ marginBottom: item.deals.length === index2 + 1 && "16px" }}
                                        isActive={
                                            popupContent !== POPUP_CONTENTS.NEW_DEAL && selectedDeal?.id === deal.id
                                        }
                                        onClick={() => {
                                            setSelectedDeal(deal);
                                            setState((prevState) => ({ ...prevState, popupContent: "Deals" }));
                                            if (isDealEditing) {
                                                setIsDealEditing(false);
                                            }
                                        }}
                                        id={`dealItem-${deal.id}`}
                                    >
                                        <DealInfoContainer>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>{deal.name}</Tooltip>}>
                                                <DealName width={dealNameWidth + "px"}>{deal.name}</DealName>
                                            </OverlayTrigger>
                                            <DealInfo>{deal.dealCampaignType}</DealInfo>
                                        </DealInfoContainer>
                                        {!deal.campaign && (
                                            <div style={{ width: "12px" }}>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>This deal has no campaign</Tooltip>}
                                                >
                                                    <i className="flaticon2-information text-primary icon-x errorIcon"></i>
                                                </OverlayTrigger>
                                            </div>
                                        )}
                                        {![DEAL_WON, DEAL_LOST, DEAL_CANCELLED].includes(deal.durum) && (
                                            <DealDurum color={textColors[index]}>{deal.durum}</DealDurum>
                                        )}
                                    </BulletlessListItem>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </List>
            ) : (
                <div
                    style={{
                        width: "100%",
                        padding: "12px",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <i className="flaticon2-warning text-warning icon-2x errorIcon"></i>
                    <div style={{ fontSize: "12px", fontWeight: "500", color: "#333" }}>No deals found</div>
                </div>
            )}
        </>
    );
};

export default ExtensionDealSideBar;
